import React, { useEffect, useMemo, useState } from "react";
import { api, downloadFile, formatINR, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, FileSpreadsheet, Mail, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import StatCard from "@/components/StatCard";
import PeriodSelector, { rangeFromValue } from "@/components/PeriodSelector";

export default function Reports() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("__all");
  const [period, setPeriod] = useState({ mode: "last", n: 3 });
  const [summary, setSummary] = useState(null);

  const { start, end } = useMemo(() => rangeFromValue(period), [period]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, []);

  useEffect(() => {
    const url =
      vehicleId === "__all"
        ? "/summary/combined"
        : `/summary/vehicle/${vehicleId}`;
    api.get(url, { params: { start, end } }).then((r) => setSummary(r.data));
  }, [vehicleId, start, end]);

  const onDownload = async () => {
    try {
      const params = { start, end };
      if (vehicleId === "__all") {
        await downloadFile(`/export/combined?start=${start}&end=${end}`, `combined_${start}_${end}.xlsx`);
      } else {
        const v = vehicles.find((x) => x.id === vehicleId);
        const fname = `${(v?.name || "vehicle").replace(/\s+/g, "_")}_${start}_${end}.xlsx`;
        await downloadFile(`/export/vehicle/${vehicleId}?start=${start}&end=${end}`, fname);
      }
      toast.success("Excel downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const [sendingEmail, setSendingEmail] = useState(false);

  const onSendMonthlyEmail = async () => {
    setSendingEmail(true);
    try {
      const res = await api.post("/reports/email-monthly");
      toast.success(
        `Monthly report emailed: ${res.data.period} → ${res.data.recipient}`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email send failed");
    } finally {
      setSendingEmail(false);
    }
  };

  const onPreviewMonthlyPdf = async () => {
    try {
      await downloadFile(`/reports/monthly-pdf`, `monthly_report.pdf`);
    } catch {
      toast.error("PDF preview failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Exports</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
            Reports
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Download Excel for any period, preview the monthly PDF, or trigger
            an instant email of last month's report.
          </p>
        </div>
      </div>

      <div className="card-swiss p-5" data-testid="monthly-email-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-emerald-600 mt-0.5" />
            <div>
              <div className="label-eyebrow">Auto-email</div>
              <h3 className="font-display text-lg font-bold tracking-tight mt-1">
                Monthly Report
              </h3>
              <p className="text-xs text-gray-500 mt-1 max-w-[520px]">
                Sent automatically on the 1st of every month at 9:00 AM IST to
                your registered email. You can also send last month's report
                instantly or preview the PDF here.
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={onPreviewMonthlyPdf}
              className="gap-2"
              data-testid="preview-monthly-pdf-btn"
            >
              <FileText className="h-4 w-4" /> Preview PDF
            </Button>
            <Button
              onClick={onSendMonthlyEmail}
              disabled={sendingEmail}
              className="gap-2"
              data-testid="send-monthly-email-btn"
            >
              {sendingEmail ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              Email Last Month
            </Button>
          </div>
        </div>
      </div>

      <div className="card-swiss p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label-eyebrow">Vehicle</label>
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger className="mt-1" data-testid="report-vehicle-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All vehicles (combined)</SelectItem>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} · {v.registration_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-eyebrow">Period</label>
            <div className="mt-1">
              <PeriodSelector value={period} onChange={setPeriod} testid="report-period" />
            </div>
          </div>
          <div className="flex items-end">
            <Button
              onClick={onDownload}
              className="w-full gap-2"
              data-testid="download-excel-btn"
            >
              <Download className="h-4 w-4" /> Download Excel
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3 font-mono">
          Range: {start} → {end}
        </p>
      </div>

      {summary && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <StatCard
              label="Run Days"
              value={summary.run_days || 0}
              sub={`${summary.halt_days || 0} halts`}
              testid="rep-run-days"
            />
            <StatCard
              label="Total Sales"
              value={formatINR(summary.total_sales || 0)}
              testid="rep-sales"
            />
            <StatCard
              label="Total Expenses"
              value={formatINR(summary.total_expenses || 0)}
              accent="text-red-600"
              testid="rep-expenses"
            />
            <StatCard
              label="Total Profit"
              value={formatINR(summary.profit || 0)}
              accent={
                (summary.profit || 0) >= 0 ? "text-emerald-700" : "text-red-600"
              }
              testid="rep-profit"
            />
            <StatCard
              label="₹ / Run Day"
              value={
                summary.run_days > 0
                  ? formatINR((summary.profit || 0) / summary.run_days)
                  : "—"
              }
              accent={
                (summary.profit || 0) >= 0 ? "text-emerald-700" : "text-red-600"
              }
              sub="Profit efficiency"
              testid="rep-ppd"
            />
          </div>

          {vehicleId === "__all" && summary.per_vehicle && (
            <div className="card-swiss p-5" data-testid="report-per-vehicle">
              <div className="flex items-center gap-2 mb-3">
                <FileSpreadsheet className="h-4 w-4 text-gray-500" />
                <div className="label-eyebrow">Per-vehicle table</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                      <th className="py-2 pr-4 font-semibold">Vehicle</th>
                      <th className="py-2 px-2 text-right font-semibold">Run</th>
                      <th className="py-2 px-2 text-right font-semibold">Sales</th>
                      <th className="py-2 px-2 text-right font-semibold">Earnings</th>
                      <th className="py-2 px-2 text-right font-semibold">Expenses</th>
                      <th className="py-2 px-2 text-right font-semibold">Profit</th>
                      <th className="py-2 px-2 text-right font-semibold">₹ / Run Day</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...summary.per_vehicle]
                      .sort((a, b) => {
                        const ra = a.run_days > 0 ? a.profit / a.run_days : -Infinity;
                        const rb = b.run_days > 0 ? b.profit / b.run_days : -Infinity;
                        return rb - ra;
                      })
                      .map((row, idx) => {
                        const ppd = row.run_days > 0 ? row.profit / row.run_days : 0;
                        const isTop = idx === 0 && row.run_days > 0;
                        return (
                      <tr key={row.vehicle.id} className="border-b border-gray-100 hover-row">
                        <td className="py-2 pr-4">
                          <div className="font-semibold flex items-center gap-2">
                            {row.vehicle.name}
                            {isTop && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 font-bold tracking-wider uppercase" data-testid="top-performer-badge">
                                Top
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">
                            {row.vehicle.registration_number}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right font-mono">{row.run_days}</td>
                        <td className="py-2 px-2 text-right font-mono">
                          {formatINR(row.total_sales)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-emerald-700">
                          {formatINR(row.total_earnings)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-red-600">
                          {formatINR(row.total_expenses)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono font-semibold">
                          {formatINR(row.profit)}
                        </td>
                        <td
                          className={`py-2 px-2 text-right font-mono font-bold ${
                            ppd >= 0 ? "text-emerald-700" : "text-red-600"
                          }`}
                          data-testid={`ppd-${row.vehicle.id}`}
                        >
                          {row.run_days > 0 ? formatINR(ppd) : "—"}
                        </td>
                      </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
